import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LOADING_STEPS = [
  { id: 'fetch', label: 'Sayfa taranıyor...', icon: 'Globe' },
  { id: 'ai', label: 'Yapay zeka analiz ediyor...', icon: 'Sparkles' },
  { id: 'save', label: 'Veritabanına kaydediliyor...', icon: 'Database' },
];

const FIXED_CATEGORIES = [
  { value: 'SUP ve Kano', label: 'SUP ve Kano' },
  { value: 'Sup Motorları ve Kürek', label: 'Sup Motorları ve Kürek' },
  { value: 'Giyim', label: 'Giyim' },
  { value: 'Aksesuar ve Yedek Parça', label: 'Aksesuar ve Yedek Parça' },
];

const SmartAddProductModal = ({ isOpen, onClose, onSuccess }) => {
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('SUP ve Kano');
  const [loadingStep, setLoadingStep] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successProduct, setSuccessProduct] = useState(null);

  const isLoading = loadingStep && loadingStep !== 'done' && loadingStep !== 'error';

  const handleClose = () => {
    if (isLoading) return;
    setUrl('');
    setCategory('SUP ve Kano');
    setLoadingStep(null);
    setErrorMessage('');
    setSuccessProduct(null);
    onClose();
  };

  const handleAddAnother = () => {
    setUrl('');
    setLoadingStep(null);
    setErrorMessage('');
    setSuccessProduct(null);
    // Keep category as-is for convenience
  };

  const handleStart = async () => {
    if (!url?.trim()) return;

    if (!category) {
      setErrorMessage('Lütfen bir kategori seçin.');
      return;
    }

    setErrorMessage('');
    setSuccessProduct(null);

    try {
      new URL(url.trim());
    } catch {
      setErrorMessage('Lütfen geçerli bir URL girin (örn: https://example.com/product)');
      return;
    }

    try {
      setLoadingStep('fetch');
      await new Promise(r => setTimeout(r, 800));

      setLoadingStep('ai');

      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !anonKey) {
        throw new Error('Supabase yapılandırması eksik. Lütfen sistem yöneticisiyle iletişime geçin.');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/smart-product-scraper`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller?.abort(), 120000);

      let response;
      try {
        response = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken || anonKey}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ url: url?.trim(), category }),
          signal: controller?.signal,
        });
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        if (fetchErr?.name === 'AbortError') {
          throw new Error('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.');
        }
        throw new Error('Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin ve tekrar deneyin.');
      }
      clearTimeout(timeoutId);

      setLoadingStep('save');
      await new Promise(r => setTimeout(r, 600));

      const result = await response?.json();

      if (!response?.ok || result?.error) {
        // Show specific message for complex/unreadable pages
        if (result?.errorCode === 'COMPLEX_PAGE') {
          throw new Error('Bu sayfanın altyapısı okunamayacak kadar karmaşık. Lütfen bu ürünü manuel ekleyiniz.');
        }
        throw new Error(result?.error || 'Bilinmeyen bir hata oluştu');
      }

      setSuccessProduct(result?.product);
      setLoadingStep('done');
      onSuccess?.();
    } catch (err) {
      setLoadingStep('error');
      setErrorMessage(err?.message || 'İşlem sırasında bir hata oluştu');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-secondary to-primary p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Icon name="Sparkles" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Link ile Akıllı Ekle</h2>
                <p className="text-white/80 text-xs">AI destekli otomatik ürün çekme</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors disabled:opacity-50"
            >
              <Icon name="X" size={16} className="text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Success State */}
          {loadingStep === 'done' && successProduct && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">Ürün Başarıyla Eklendi!</h3>
                <p className="text-sm text-muted-foreground mt-1">{successProduct?.title}</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleAddAnother} className="flex-1">
                  Başka Ürün Ekle
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  Kapat
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {loadingStep === 'error' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-error/10 border border-error/20">
                <Icon name="AlertCircle" size={20} className="text-error mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-error">Hata Oluştu</p>
                  <p className="text-sm text-error/80 mt-1">{errorMessage}</p>
                </div>
              </div>
              <Button variant="outline" onClick={handleAddAnother} className="w-full">
                Tekrar Dene
              </Button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              {LOADING_STEPS?.map((step, index) => {
                const stepIndex = LOADING_STEPS?.findIndex(s => s?.id === loadingStep);
                const isDone = index < stepIndex;
                const isActive = step?.id === loadingStep;
                return (
                  <div key={step?.id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isActive ? 'bg-primary/10 border border-primary/20' : isDone ? 'opacity-60' : 'opacity-30'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-primary text-white' : isDone ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                      {isDone ? <Icon name="Check" size={16} /> : isActive ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name={step?.icon} size={16} />}
                    </div>
                    <span className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>{step?.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input Form */}
          {!isLoading && loadingStep !== 'done' && loadingStep !== 'error' && (
            <div className="space-y-4">
              <Input
                label="Ürün URL'si"
                value={url}
                onChange={(e) => setUrl(e?.target?.value)}
                placeholder="https://example.com/product"
                type="url"
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Kategori</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e?.target?.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary bg-card text-foreground text-sm"
                >
                  {FIXED_CATEGORIES?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Yapay zeka da bu kategoriyi referans alacak.</p>
              </div>

              {errorMessage && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/20">
                  <Icon name="AlertCircle" size={16} className="text-error mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-error">{errorMessage}</p>
                </div>
              )}

              <Button
                onClick={handleStart}
                disabled={!url?.trim() || !category}
                className="w-full"
              >
                <Icon name="Sparkles" size={16} className="mr-2" />
                Ürünü Analiz Et ve Ekle
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartAddProductModal;
